/**
 * The item list's active-filter shape and the status vocabulary — shared by `items.tsx` (which
 * owns the filter state and the list query) and `filter-sheet.tsx` (frame 3c, which edits a draft
 * of it). It lives here, not in `items.tsx`, so the sheet can import the type without a cycle
 * (`items.tsx` imports the sheet).
 */

import { NO_KIND_LABEL, SOURCE_KIND_LABELS, SOURCE_KIND_VALUES, type SourceKind } from '@mononoke/shared';
import { FILTER_NO_KIND, FILTER_NO_SOURCE } from './box-filters.js';

export interface ActiveFilters {
  category?: { id: string; path: string } | undefined;
  /** A SET of statuses, OR-combined (frame 3c multi-select). Absent/empty = no status filter. */
  status?: string[] | undefined;
  /** Only items with no photo of any variant. A predicate, so it is on or absent. */
  withoutPhoto?: boolean | undefined;
  /** Only items with no garment barcode linked (090, 0.1.515) — the backlog of the 1,940. */
  withoutBarcode?: boolean | undefined;
  /**
   * THE ITEM'S OWN SOURCE (068), as two SETS (0.1.395, maintainer 26 Aug). Each holds source ids
   * (or `SOURCE_KIND_VALUES` for the type) plus, possibly, the null sentinel — the same shape and
   * the same sentinels the boxes filter sheet has used since 0.1.370, imported rather than
   * re-declared so "no source yet" cannot come to mean two different strings.
   *
   * ⚠️ IDS, NOT NAMES, for the source. A rename must not silently empty somebody's filter, and two
   * sources may not share a name today but nothing stops them tomorrow. The chips look the name up
   * from the list the sheet already holds.
   */
  sources?: string[] | undefined;
  sourceKinds?: string[] | undefined;
  fields: Record<string, { label: string; value: string }>;
}

/** The five filterable statuses and how they read (frame 3c's STATUS pills, the chips, and the
 *  inline quick-select all draw from this one list, so a label cannot say one thing in the pill
 *  and another in the chip). `flagged` leads — it is the one an atelier opens the filter for. */
export const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'flagged', label: 'Flagged' },
  { value: 'available', label: 'Available' },
  { value: 'checked_out', label: 'Checked out' },
  { value: 'in_custody', label: 'In custody' },
  { value: 'retired', label: 'Retired' },
];

export const STATUS_LABELS: Record<string, string> = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.label]));

/** The dimensions' words — the chips and the picker rows read from here (0.1.456). */
export const ITEM_DIMENSION_LABELS = {
  status: 'Status',
  category: 'Category',
  sources: 'Source',
  sourceKinds: 'Source type',
  withoutPhoto: 'Photo',
  withoutBarcode: 'Barcode',
} as const;

/**
 * THE ACTIVE FILTERS AS CHIPS (0.1.456) — the one derivation the desk bar, the desk panel
 * and the phone Display sheet all render (`FilterChips`). Until 0.1.456 the items screen
 * carried its own chip markup; this is the same list as data. Source chips look their NAME up
 * (the stored value is an id) and say so when the lookup fails — never a uuid at somebody.
 */
export function describeItemFilters(
  filters: ActiveFilters,
  sourceNames: ReadonlyMap<string, string>,
): { key: string; dim: string; label: string }[] {
  const L = ITEM_DIMENSION_LABELS;
  const chips: { key: string; dim: string; label: string }[] = [];
  for (const s of filters.status ?? []) chips.push({ key: `status:${s}`, dim: L.status, label: STATUS_LABELS[s] ?? s });
  if (filters.category) chips.push({ key: 'category', dim: L.category, label: filters.category.path });
  for (const s of filters.sources ?? []) {
    chips.push({ key: `sources:${s}`, dim: L.sources, label: s === FILTER_NO_SOURCE ? 'No source yet' : (sourceNames.get(s) ?? 'a deleted source') });
  }
  for (const k of filters.sourceKinds ?? []) {
    chips.push({ key: `sourceKinds:${k}`, dim: L.sourceKinds, label: k === FILTER_NO_KIND ? NO_KIND_LABEL : (SOURCE_KIND_LABELS[k as SourceKind] ?? k) });
  }
  if (filters.withoutPhoto === true) chips.push({ key: 'withoutPhoto', dim: L.withoutPhoto, label: 'No photo yet' });
  if (filters.withoutBarcode === true) chips.push({ key: 'withoutBarcode', dim: L.withoutBarcode, label: 'none' });
  for (const [key, f] of Object.entries(filters.fields)) chips.push({ key: `field:${key}`, dim: f.label, label: f.value });
  return chips;
}

/** Take exactly one chip back out, by its key. */
export function removeItemFilter(filters: ActiveFilters, key: string): ActiveFilters {
  const at = key.indexOf(':');
  const dim = at === -1 ? key : key.slice(0, at);
  const value = at === -1 ? '' : key.slice(at + 1);
  if (dim === 'category') return { ...filters, category: undefined };
  if (dim === 'withoutPhoto') return { ...filters, withoutPhoto: undefined };
  if (dim === 'withoutBarcode') return { ...filters, withoutBarcode: undefined };
  if (dim === 'field') {
    const fields = { ...filters.fields };
    delete fields[value];
    return { ...filters, fields };
  }
  if (dim === 'status' || dim === 'sources' || dim === 'sourceKinds') {
    const next = (filters[dim] ?? []).filter((v) => v !== value);
    return { ...filters, [dim]: next.length > 0 ? next : undefined };
  }
  return filters;
}

/**
 * THE PICKER'S DIMENSIONS (0.1.456), in plain words with a hint each. Status leads — it is the
 * filter an atelier opens this for. Category and every select-kind field are single-valued
 * (the server's contract); the fields allow free entry because values free-typed at the rack
 * exist on items without being options. Sources are offered only once the list is read.
 */
export function itemFilterDimensions(
  meta: { categories: { id: string; path: string }[]; fields: { key: string; label: string; kind: string; options: string[] }[] } | null,
  sources: { id: string; name: string }[] | null,
  filters: ActiveFilters,
  set: (next: ActiveFilters) => void,
  /**
   * THE VALUES PRESENT ON THE LOADED ROWS, per field LABEL (0.1.505; maintainer, 10 Sep 2026: "get me
   * the colour filter"). A text field has no option list of its own — the atelier's Colour is one —
   * so its picker row lists what the rows actually carry, and the free entry stays for a value not
   * loaded yet. Empty (the scoped flat lists) means options come from the definition alone.
   */
  present: Record<string, string[]> = {},
): {
  id: string;
  label: string;
  hint: string;
  options: { value: string; label: string }[];
  selected: string[];
  multi: boolean;
  allowFree?: boolean;
  toggle: (value: string) => void;
}[] {
  const L = ITEM_DIMENSION_LABELS;
  const toggleSet = (dim: 'status' | 'sources' | 'sourceKinds', value: string): void => {
    const current = filters[dim] ?? [];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    set({ ...filters, [dim]: next.length > 0 ? next : undefined });
  };
  const out: ReturnType<typeof itemFilterDimensions> = [
    {
      id: 'status',
      label: L.status,
      hint: 'Available, checked out, flagged…',
      options: STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label })),
      selected: filters.status ?? [],
      multi: true,
      toggle: (v) => toggleSet('status', v),
    },
    {
      id: 'category',
      label: L.category,
      hint: 'One branch of the catalogue tree',
      options: (meta?.categories ?? []).map((c) => ({ value: c.id, label: c.path })),
      selected: filters.category ? [filters.category.id] : [],
      multi: false,
      toggle: (v) => {
        const found = (meta?.categories ?? []).find((c) => c.id === v);
        set({ ...filters, category: filters.category?.id === v || found === undefined ? undefined : { id: found.id, path: found.path } });
      },
    },
    ...(sources !== null && sources.length > 0
      ? [
          {
            id: 'sources',
            label: L.sources,
            hint: 'Who the garment came from',
            options: [{ value: FILTER_NO_SOURCE, label: 'No source yet' }, ...sources.map((s) => ({ value: s.id, label: s.name }))],
            selected: filters.sources ?? [],
            multi: true,
            toggle: (v: string) => toggleSet('sources', v),
          },
        ]
      : []),
    {
      id: 'sourceKinds',
      label: L.sourceKinds,
      hint: 'Lender, shop, designer…',
      options: [{ value: FILTER_NO_KIND, label: NO_KIND_LABEL }, ...SOURCE_KIND_VALUES.map((k) => ({ value: k, label: SOURCE_KIND_LABELS[k] }))],
      selected: filters.sourceKinds ?? [],
      multi: true,
      toggle: (v) => toggleSet('sourceKinds', v),
    },
    {
      id: 'withoutPhoto',
      label: L.withoutPhoto,
      hint: 'Only the garments nobody has photographed',
      options: [{ value: 'none', label: 'No photo yet' }],
      selected: filters.withoutPhoto === true ? ['none'] : [],
      multi: false,
      toggle: () => set({ ...filters, withoutPhoto: filters.withoutPhoto === true ? undefined : true }),
    },
    {
      id: 'withoutBarcode',
      label: L.withoutBarcode,
      hint: 'Only the garments with no barcode linked yet',
      options: [{ value: 'none', label: 'none' }],
      selected: filters.withoutBarcode === true ? ['none'] : [],
      multi: false,
      toggle: () => set({ ...filters, withoutBarcode: filters.withoutBarcode === true ? undefined : true }),
    },
    /* EVERY FIELD, EVERY KIND (0.1.505). The `.filter(kind === select)` that stood here is why a
       text field could not be filtered — the API's filter never cared about the kind
       (`items.ts`, the EXISTS on item_field_values: field_id AND value). Options are the
       definition's list ∪ the values present on the rows, sorted; the free entry stays. */
    ...(meta?.fields ?? []).map((f) => ({
        id: `field:${f.key}`,
        label: f.label,
        hint: `One ${f.label.toLowerCase()} — or type one`,
        options: [...new Set([...f.options, ...(present[f.label] ?? [])])]
          .sort((a, b) => a.localeCompare(b))
          .map((o) => ({ value: o, label: o })),
        selected: filters.fields[f.key] ? [filters.fields[f.key]!.value] : [],
        multi: false,
        allowFree: true,
        toggle: (v: string) => {
          const fields = { ...filters.fields };
          if (fields[f.key]?.value === v) delete fields[f.key];
          else fields[f.key] = { label: f.label, value: v };
          set({ ...filters, fields });
        },
      })),
  ];
  return out;
}

/** Statuses reduce to one dimension however many are chosen — the "N on" count in the frame is
 *  the number of DIMENSIONS touched, not the number of status pills lit. */
export function countFilters(filters: ActiveFilters): number {
  return (
    (filters.category ? 1 : 0) +
    (filters.status !== undefined && filters.status.length > 0 ? 1 : 0) +
    (filters.withoutPhoto === true ? 1 : 0) +
    (filters.withoutBarcode === true ? 1 : 0) +
    (filters.sources !== undefined && filters.sources.length > 0 ? 1 : 0) +
    (filters.sourceKinds !== undefined && filters.sourceKinds.length > 0 ? 1 : 0) +
    Object.keys(filters.fields).length
  );
}

const strings = (v: unknown): string[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter((x): x is string => typeof x === 'string');
  return out.length > 0 ? out : undefined;
};

/**
 * A STORED Items filter set (0.1.522 — filters persist, display/use-stored-filters.ts), made safe
 * to trust. Shape only, field by field: a value of the wrong type is dropped, never thrown, and the
 * result is always a valid `ActiveFilters`. A status this build no longer knows is dropped too —
 * it would be a chip nobody can find in the sheet. Ids (sources, category) are kept as stored: a
 * deleted one narrows to nothing, and its chip is the way to remove it.
 */
export function sanitizeItemFilters(raw: unknown): ActiveFilters | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const out: ActiveFilters = { fields: {} };
  const cat = r.category as { id?: unknown; path?: unknown } | undefined;
  if (typeof cat === 'object' && cat !== null && typeof cat.id === 'string' && typeof cat.path === 'string') {
    out.category = { id: cat.id, path: cat.path };
  }
  const status = strings(r.status)?.filter((s) => STATUS_LABELS[s] !== undefined);
  if (status !== undefined && status.length > 0) out.status = status;
  if (r.withoutPhoto === true) out.withoutPhoto = true;
  if (r.withoutBarcode === true) out.withoutBarcode = true;
  const sources = strings(r.sources);
  if (sources !== undefined) out.sources = sources;
  const sourceKinds = strings(r.sourceKinds);
  if (sourceKinds !== undefined) out.sourceKinds = sourceKinds;
  if (typeof r.fields === 'object' && r.fields !== null && !Array.isArray(r.fields)) {
    for (const [key, f] of Object.entries(r.fields as Record<string, unknown>)) {
      const field = f as { label?: unknown; value?: unknown } | null;
      if (typeof field === 'object' && field !== null && typeof field.label === 'string' && typeof field.value === 'string') {
        out.fields[key] = { label: field.label, value: field.value };
      }
    }
  }
  return out;
}
